import {
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS
} from '@angular/common/http';
import { ApplicationConfig, inject, provideAppInitializer } from '@angular/core';
import { provideRouter, RouteReuseStrategy, withHashLocation } from '@angular/router';
import {
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication
} from '@azure/msal-browser';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { SI_COPYRIGHT_DETAILS } from '@siemens/element-ng/copyright-notice';
import { provideSiDatatableConfig, SI_DATATABLE_CONFIG } from '@siemens/element-ng/datatable';
import {
  provideMissingTranslationHandlerForElement,
  provideNgxTranslateForElement
} from '@siemens/element-translate-ng/ngx-translate';
import { firstValueFrom } from 'rxjs';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { apiConfig, msalConfig } from './auth/msal-config';
import { CustomRouteReuseStrategyService } from './custom-route-reuse-strategy.service';
import {
  BROUTER_MAP_CONFIG,
  BrouterMapConfig,
  brouterEnginePath,
  defaultBrouterMapConfig
} from './main/shared/brouter-map/config/brouter-map.config';
import { getBaseUrl } from './main/shared/services/share-data.service';

// Helper function to convert API base URL to a pattern for MSAL protected resource map
// e.g. "http://localhost:5314/api" -> "http://localhost:5314/api/*"
const getResourcePattern = (baseUrl: string): string => {
  return `${baseUrl.replace(/\/+$/, '')}/*`;
};

// Factory function to create MSAL instance
export const msalInstanceFactory = (): IPublicClientApplication => {
  return new PublicClientApplication({
    auth: {
      clientId: msalConfig.auth.clientId,
      authority: msalConfig.auth.authority,
      redirectUri: getBaseUrl(),
      postLogoutRedirectUri: '/'
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      loggerOptions: {
        loggerCallback: () => {},
        piiLoggingEnabled: false
      }
    }
  });
};

// Factory function to create MSAL Interceptor configuration
export const msalInterceptorConfigFactory = (): MsalInterceptorConfiguration => {
  const protectedResourceMap = new Map<string, string[]>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me/photo/$value', ['user.read']);

  // Needed to ensure that Bearer token is sent to our API.
  // /access_as_user scope is defined in Azure AD app registration
  protectedResourceMap.set(getResourcePattern(environment.apiURL), [
    `api://${msalConfig.auth.clientId}/access_as_user`
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
};

// Factory function to create MSAL Guard configuration
export const msalGuardConfigFactory = (): MsalGuardConfiguration => {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...apiConfig.scopes]
    },
    loginFailedRoute: '/login-failed'
  };
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideRouter(routes, withHashLocation()),
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategyService },
    provideHttpClient(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: 'assets/i18n/',
        suffix: '.json'
      }),
      fallbackLang: 'en'
    }),
    provideNgxTranslateForElement(),
    provideMissingTranslationHandlerForElement(),
    {
      provide: SI_COPYRIGHT_DETAILS,
      useValue: {
        startYear: 2024
      }
    },
    provideSiDatatableConfig({ rowHeight: SI_DATATABLE_CONFIG.rowHeightSmall }),
    {
      provide: BROUTER_MAP_CONFIG,
      useValue: {
        ...defaultBrouterMapConfig,
        // Hash routing keeps `pathname` at the serving path, so this holds on deep links too.
        host: `${getBaseUrl().replace(/\/+$/, '')}/${brouterEnginePath}`
      } satisfies BrouterMapConfig
    },
    // MSAL Providers
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: msalInstanceFactory
    },
    // Initialize MSAL instance on app startup
    provideAppInitializer(() => inject(MSAL_INSTANCE).initialize()),
    // Wait for the redirect handling to complete before allowing the app to bootstrap
    provideAppInitializer(() => firstValueFrom(inject(MsalService).handleRedirectObservable())),
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: msalGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: msalInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  ]
};
